import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OvertimeReason } from "@/types/supabase-types";
import { useAuth } from "@/contexts/auth";

export function useOvertimeReasons() {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch overtime reasons filtered by company_id and is_active
  const {
    data: overtimeReasons = [],
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['overtime_reasons', user?.companyId],
    queryFn: async (): Promise<OvertimeReason[]> => {
      if (!user?.companyId) {
        setError('User company ID not available');
        return [];
      }

      const { data, error } = await supabase
        .from('overtime_reasons')
        .select('*')
        .eq('company_id', user.companyId)
        .eq('is_active', true)
        .order('main_title', { ascending: true })
        .order('reason', { ascending: true });

      if (error) {
        setError(error.message);
        throw error;
      }

      setError(null);
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user?.companyId,
  });

  // Add/Update overtime reason using RPC function
  const upsertReasonMutation = useMutation({
    mutationFn: async (data: { id?: string; main_title: string; reason: string; code?: string }): Promise<{ success: boolean; data?: OvertimeReason; message?: string; error?: string }> => {
      if (!user?.companyId) {
        return { success: false, error: 'User company ID not available' };
      }

      // Don't pass code - let SQL function generate it
      const { data: result, error } = await (supabase as any).rpc('upsert_overtime_reason', {
        p_main_title: data.main_title,
        p_reason: data.reason,
        p_company_id: user.companyId,
        p_id: data.id || null,
        p_code: null // Let SQL function handle code generation
      });

      if (error) {
        console.error('Error upserting overtime reason:', error);
        return { success: false, error: error.message };
      }

      return result;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['overtime_reasons', user?.companyId] });
      const previousReasons = queryClient.getQueryData(['overtime_reasons', user?.companyId]);

      if (data.id) {
        // UPDATE: Update existing reason in local state
        queryClient.setQueryData(['overtime_reasons', user?.companyId], (old: OvertimeReason[] | undefined) => {
          if (!old) return old;
          return old.map(reason => 
            reason.id === data.id 
              ? { 
                  ...reason, 
                  main_title: data.main_title, 
                  reason: data.reason, 
                  // Don't set code here - it will come from the server response
                  date_created: new Date().toISOString()
                }
              : reason
          );
        });
      } else {
        // INSERT: Add new reason to local state
        const newReason: OvertimeReason = {
          id: `temp-${Date.now()}`,
          main_title: data.main_title,
          reason: data.reason,
          code: 'Generating...', // Placeholder until server responds
          company_id: user?.companyId || '',
          is_active: true,
          date_created: new Date().toISOString()
        };
        
        queryClient.setQueryData(['overtime_reasons', user?.companyId], (old: OvertimeReason[] | undefined) => {
          if (!old) return [newReason];
          return [...old, newReason].sort((a, b) => 
            a.main_title.localeCompare(b.main_title) || (a.reason || '').localeCompare(b.reason || '')
          );
        });
      }

      return { previousReasons };
    },
    onError: (err, data, context) => {
      if (context?.previousReasons) {
        queryClient.setQueryData(['overtime_reasons', user?.companyId], context.previousReasons);
      }
      setError('Failed to save overtime reason');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime_reasons', user?.companyId] });
      queryClient.invalidateQueries({ queryKey: ['overtime_reasons_main_titles', user?.companyId] });
      setError(null);
    }
  });

  // Delete overtime reason (soft delete by setting is_active to false)
  const deleteReasonMutation = useMutation({
    mutationFn: async (id: string): Promise<boolean> => {
      if (!user?.companyId) return false;

      const { error } = await (supabase as any)
        .from('overtime_reasons')
        .update({ is_active: false })
        .eq('id', id)
        .eq('company_id', user.companyId);

      if (error) {
        console.error('Error deleting overtime reason:', error);
        return false;
      }

      return true;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['overtime_reasons', user?.companyId] });
      const previousReasons = queryClient.getQueryData(['overtime_reasons', user?.companyId]);

      queryClient.setQueryData(['overtime_reasons', user?.companyId], (old: OvertimeReason[] | undefined) => {
        if (!old) return old;
        return old.filter(reason => reason.id !== id);
      });

      return { previousReasons };
    },
    onError: (err, id, context) => {
      if (context?.previousReasons) {
        queryClient.setQueryData(['overtime_reasons', user?.companyId], context.previousReasons);
      }
      setError('Failed to delete overtime reason');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime_reasons'] });
      queryClient.invalidateQueries({ queryKey: ['overtime_reasons_main_titles'] });
      setError(null);
    }
  });

  // Wrapper functions - return only success/failure, let component handle toasts
  const addReason = async (data: { main_title: string; reason: string; code?: string }): Promise<boolean> => {
    const result = await upsertReasonMutation.mutateAsync(data);
    return result.success;
  };

  const updateReason = async (data: { id: string; main_title: string; reason: string; code?: string }): Promise<boolean> => {
    const result = await upsertReasonMutation.mutateAsync(data);
    return result.success;
  };

  const deleteReason = async (id: string): Promise<boolean> => {
    return deleteReasonMutation.mutateAsync(id);
  };

  return {
    overtimeReasons,
    isLoading,
    error,
    addReason,
    updateReason,
    deleteReason,
    refetch
  };
}
